import {useState} from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import isArray from 'lodash/isArray';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import DayAvailability from './DayAvailability';
import {getDays, validateAvailabilityTimes} from '../../helpers/scheduling';

const days = getDays();

const getBlankTimesDict = (_days = days) => _days.reduce(
  (acc, d) => ({
    ...acc,
    [d]: (
      ['SAT', 'SUN'].includes(d)
      ? []
      : [
        {start_time: '09:00 am', end_time: '12:00 pm'},
        {start_time: '12:30 pm', end_time: '5:00 pm'},
      ]
    )
  }),
  {}
);

const parseDefaultValue = (defaultValue, _days = days) => defaultValue.reduce(
  (acc, next) => ({
    ...acc,
    [next.day]: (
      acc[next.day]
      ? [...acc[next.day], omit(next, 'day')]
      : [omit(next, 'day')]
    ),
  }),
  _days.reduce((acc, d) => ({...acc, [d]: []}), {})
);

const getInitialState = props => {
  return (
    props.availabilities && props.availabilities.length
    // if providerAvailabilities are present, parse into a dictionary
    ? props.availabilities.reduce(
      (acc, next) => ({
        ...acc,
        [next.day]: (
          acc[next.day]
          ? [...acc[next.day], pick(next, 'start_time', 'end_time')]
          : [pick(next, 'start_time', 'end_time')]
        ),
      }),
      {}
    )
    : (
      props.defaultValue && isArray(props.defaultValue)
      ? parseDefaultValue(props.defaultValue)
      : getBlankTimesDict()
    )
  );
};

const parseFormValuesToAvailabilityObjects = sessionTypeId => timesDict => {
  const availabilityObjects = Object.keys(timesDict).reduce((acc, day) => {
    const times = timesDict[day];
    const objects = times
      .map(t => ({
        day,
        session_type_id: sessionTypeId,
        start_time: t.start_time,
        end_time: t.end_time,
      }))
      .map(a => sessionTypeId ? a : omit(a, 'session_type_id'));
    return [...acc, ...objects];
  }, []);
  return availabilityObjects;
};

const handleTimeDictUpdate = ({
  timesDict,
  setTimesDict,
  onChange,
  sessionTypeId
}) => day => newTimes => {
  const parser = parseFormValuesToAvailabilityObjects(sessionTypeId);
  const newValues = {
    ...timesDict,
    [day]: newTimes,
  };
  onChange({
    target: {
      value: parser(newValues),
    }
  });
  setTimesDict(newValues);
};

const AvailabilityInput = props => {
  const [initialTimes] = useState(getInitialState(props));
  const [timesDict, setTimesDict] = useState(getInitialState(props));;
  const onChangeHandler = handleTimeDictUpdate({
    timesDict,
    setTimesDict,
    onChange: props.onChange,
    sessionTypeId: props.sessionTypeId,
  });
  const timesAreValid = validateAvailabilityTimes(
    Object.values(timesDict).flatMap(arr => arr)
  );
  return (
    <Box>
      <Stack spacing={2}>
        <Typography variant='formLabel'>
          {props.label}
        </Typography>
        {timesAreValid
          ? null
          : <Alert severity='error'>
            At least one of these times is invalid.
          </Alert>
        }
        {/* Add an Availability editor for each day */}
        {days.map(d =>
          <DayAvailability
            key={d}
            day={d}
            defaultTimes={initialTimes[d]}
            onChange={onChangeHandler(d)}
          />
        )}
      </Stack>
    </Box>
  );
};

AvailabilityInput.propTypes = {
  availabilities: PropTypes.arrayOf(PropTypes.shape({
    start_time: PropTypes.string,
    end_time: PropTypes.string,
  })),
};

export default AvailabilityInput;
